import * as React from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { track } from '@amplitude/analytics-browser'
import { Box } from '@factorialco/deprecated-design-system'
import { BillingPlansPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import DeleteCompanyAction from 'modules/billing/components/DeleteCompanyAction'
import PaymentActionRequiredModal from 'modules/billing/components/PaymentActionRequiredModal'
import YourLegacyPlan from 'modules/billing/components/YourLegacyPlan'
import YourPlan from 'modules/billing/components/YourPlan'
import plans from 'modules/billing/store/collections/plans'
import subscriptions from 'modules/billing/store/collections/subscriptions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { YourRecruitmentPlan } from './YourRecruitmentPlan'

const Plans = () => {
  const [isPaymentActionRequired, setIsPaymentActionRequired] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const { pathname } = useLocation()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { company } = useCurrentSession()
  const subscriptionId = company?.subscription?.id

  const { data, error } = useQuery(BillingPlansPageDocument, {
    variables: {
      subscriptionId,
    },
    skip: !subscriptionId,
  })

  const managedByPartner = hasFeature('managed_by_partner')

  const renderActionRequired = () => {
    if (!isPaymentActionRequired) return null

    return <PaymentActionRequiredModal />
  }

  const renderContent = () => {
    if (isLoading) return <Loading />

    const handlePlanUpdate = () => {
      track(trackingEvents.billing.goToPricingPlans, {
        page_path: pathname,
        from: 'settings_upgrade_plan_button',
      })
      navigate(`/${navigation.upgradePlan.path}`)
    }

    return (
      <Box gap="s24">
        {!hasFeature('dev_chargebee') && <YourLegacyPlan onCTAClick={handlePlanUpdate} />}
        <YourPlan
          expensesUserCount={data?.features.featureMembership?.membersConnection.totalCount ?? 0}
          managedByPartnersUserLimit={data?.billing.subscription?.expensesQuantityCeiling ?? 0}
          onCTAClick={handlePlanUpdate}
        />
        <YourRecruitmentPlan onCTAClick={handlePlanUpdate} />
        <Outlet context={{ onPaymentActionRequired: () => setIsPaymentActionRequired(true) }} />
        {renderActionRequired()}
        <DeleteCompanyAction />
      </Box>
    )
  }

  useEffect(() => {
    const fetchData = async () => {
      const promises = [
        subscriptions.fetch({
          data: {
            next_invoice: true,
          },
        }),
        plans.fetch(),
      ]
      await subscriptions.rpc('sync')

      const { success } = await asyncAction(
        () => {
          return Promise.all(promises)
        },
        {
          successFlash: false,
        }
      )

      if (success) setIsLoading(false)
    }

    fetchData()
  }, [])

  return (
    <ResponsivePage.Body
      header={{
        title: i18n.t('company_settings.general.subscription.title'),
      }}
      loading={isLoading || (!data && !error)}
      error={error}
    >
      {renderContent()}
    </ResponsivePage.Body>
  )
}

export default observer(Plans)
